import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Empty, Skeletons, SectionHead } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mic2, Search, Users, AlertTriangle } from "lucide-react";

export default function Participants() {
  const [users, setUsers] = useState([]);
  const [speakerStats, setSpeakerStats] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/dashboard/stats")])
      .then(([u, s]) => { setUsers(u.data); setSpeakerStats(s.data.speaker_stats || []); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const shown = users.filter((u) => !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-7" data-testid="participants-page">
      <div>
        <p className="label-xs">Direktori</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Participants</h1>
        <p className="mt-2 text-sm text-slate-500">{users.length} peserta · {users.filter((u) => u.voice_enrolled).length} memiliki voiceprint aktif.</p>
      </div>

      <div className="clay p-5">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau email peserta…" className="h-11 pl-10 rounded-xl" data-testid="participant-search-input" />
        </div>
      </div>

      {loading ? <Skeletons rows={3} className="h-32" /> : shown.length === 0 ? (
        <div className="clay"><Empty icon={Users} title="Peserta tidak ditemukan" sub="Coba ubah kata kunci pencarian." /></div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="participants-grid">
          {shown.map((u, i) => {
            const st = speakerStats.find((s) => s.name === u.name);
            return (
              <div key={u.id} className="clay clay-hover p-6 animate-fade-up" style={{ animationDelay: `${Math.min(i * 40, 320)}ms` }} data-testid={`participant-card-${u.id}`}>
                <div className="flex items-start gap-4">
                  <span className="grid place-items-center h-12 w-12 shrink-0 rounded-full grad-brand text-white text-sm font-semibold">
                    {u.name.slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-base font-semibold text-slate-900">{u.name}</p>
                    <p className="truncate text-xs text-slate-500">{u.email}</p>
                    <span className="mt-2 inline-block rounded-full bg-brand-50 px-2.5 py-0.5 text-[11px] font-medium text-brand-700">{u.role_label}</span>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4">
                  <div>
                    <p className="label-xs">Divisi</p>
                    <p className="mt-1 truncate text-sm text-slate-700">{u.division || "—"}</p>
                  </div>
                  <div>
                    <p className="label-xs">Segmen</p>
                    <p className="mt-1 font-mono text-sm text-slate-700">{st?.segments ?? 0}</p>
                  </div>
                </div>
                <div className="mt-4">
                  {u.voice_enrolled ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                      <Mic2 size={12} /> Voiceprint aktif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                      <AlertTriangle size={12} /> Belum enroll
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
